import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { API, useFetch } from 'app/api';
import { Icon } from 'app/design-system/components/Icon';
import { ICON_NAME_BELL } from 'app/design-system/constants/IconNames';
import { getUnreadNotificationsCount } from 'app/lib/notifications/notifications.actions';
import { setUnreadNotificationsCount } from 'app/lib/notifications/reducers/notifications.slice';
import { selectUnreadNotificationsCount } from 'app/lib/notifications/reducers/selectors/notifications.selectors';
import { createGaEvent, dispatchGaEvent } from 'app/packages/analytics';

import DropdownMenu from '../DropdownMenu';
import { DropdownFooter } from './components/DropdownFooter';
import { UnreadNotification } from './components/UnreadNotification';

import styles from './NotificationsBell.module.scss';

export const NotificationsBell: FC = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const markReadAllNotificationsAPI = useFetch(
    API.notifications.markReadAllNotifications,
    { skipSSR: true },
  );

  const unreadCount = useSelector(selectUnreadNotificationsCount);

  const handleToggle = useCallback(() => {
    setIsOpen((currentState) => {
      if (currentState) {
        markReadAllNotificationsAPI.makeRequest();
        dispatch(setUnreadNotificationsCount(0));
      }
      else {
        dispatchGaEvent(createGaEvent({ event: 'header_notifications_bell_click' }));
      }
      return !currentState;
    });
  }, []);

  useEffect(() => {
    dispatch(getUnreadNotificationsCount());
  }, []);

  const icon = useMemo(
    () => (
      <Icon className={classNames(styles.icon, { [styles.unreadCircle]: unreadCount })}
            icon={ICON_NAME_BELL} />
    ),
    [unreadCount],
  );

  return (
    <DropdownMenu className={styles.root}
                  data-test="headerNotificationsBell-dropdownWrapper"
                  headerLabel={icon}
                  onToggle={handleToggle}
                  isOpen={isOpen}
                  hideIcon>
      <UnreadNotification unreadCount={unreadCount}
                          onClick={handleToggle} />
      {!!unreadCount && (
        <DropdownFooter unreadCount={unreadCount}
                        onClick={handleToggle} />
      )}
    </DropdownMenu>
  );
};
